/**
 * Utility functions for file type validation and detection
 */

export class MediaAllowed {
  static VIDEOS = ['.mp4'] as const;
  static IMAGES = ['.jpg', '.jpeg', '.png'] as const;
  static AUDIOS = ['.mp3'] as const;
  static DOCUMENTS = ['.ppt', '.pptx', '.xls', '.xlsx', '.doc', '.docx', '.pdf'] as const;
  static ALL = [...MediaAllowed.VIDEOS, ...MediaAllowed.IMAGES, ...MediaAllowed.AUDIOS, ...MediaAllowed.DOCUMENTS];
}
export type MediaAllowedKeys = keyof Omit<typeof MediaAllowed, 'prototype'>;

export type FileExtensions = typeof MediaAllowed['ALL'][number];

export function normalizeFileExtensions(value: MediaAllowedKeys | (FileExtensions | MediaAllowedKeys)[]): FileExtensions[] {
  if (typeof value === 'string') {
    return MediaAllowed[value] as FileExtensions[];
  }
  return value.map(key => {
    if (isObjKey(key, MediaAllowed)) {
      return MediaAllowed[key];
    }
    return key;
  }).flat();
}

function isObjKey<T extends object>(key: PropertyKey, obj: T): key is keyof T {
  return key in obj;
}
/**
 * Checks if a file is a video based on its extension
 * @param fileName The name of the file
 * @returns True if the file is a video, false otherwise
 */
export function isVideoFile(fileName: string): boolean {
  const videoExtensions = ['.mp4'];
  const extension = getFileExtension(fileName);
  return videoExtensions.includes(extension);
}

/**
 * Checks if a file is an image based on its extension
 * @param fileName The name of the file
 * @returns True if the file is an image, false otherwise
 */
export function isImageFile(fileName: string): boolean {
  const imageExtensions = ['.jpg', '.jpeg', '.png'];
  const extension = getFileExtension(fileName);
  return imageExtensions.includes(extension);
}

/**
 * Checks if a file is a audio based on its extension
 * @param fileName The name of the file
 * @returns True if the file is a audio, false otherwise
 */
export function isAudioFile(fileName: string): boolean {
  const audioExtensions = ['.mp3'];
  const extension = getFileExtension(fileName);
  return audioExtensions.includes(extension);
}

/**
 * Checks if a file is a document based on its extension
 * @param fileName The name of the file
 * @returns True if the file is a document, false otherwise
 */
export function isDocumentFile(fileName: string): boolean {
  const documentExtensions = ['.ppt', '.pptx', '.xls', '.xlsx', '.doc', '.docx', '.pdf'];
  const extension = getFileExtension(fileName);
  return documentExtensions.includes(extension);
}

/**
 * Checks if a file is a document based on its extension
 * @param fileName The name of the file
 * @returns True if the file is a document, false otherwise
 */
export function isPdfFile(fileName: string): boolean {
  const documentExtensions = ['.pdf'];
  const extension = getFileExtension(fileName);
  return documentExtensions.includes(extension);
}
/**
 * Checks if a file is a document based on its extension
 * @param fileName The name of the file
 * @returns True if the file is a document, false otherwise
 */
export function isDocFile(fileName: string): boolean {
  const documentExtensions = ['.ppt', '.pptx', '.xls', '.xlsx', '.doc', '.docx'];
  const extension = getFileExtension(fileName);
  return documentExtensions.includes(extension);
}



/**
 * Gets the file extension from a filename
 * @param fileName The name of the file
 * @returns The file extension in lowercase, including the dot
 */
export function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex === -1) return '';
  return fileName.substring(lastDotIndex).toLowerCase();
}

/**
 * Checks if a file type is allowed based on video selection settings
 * @param fileName The name of the file
 * @param allowVideoSelection Whether video files are allowed
 * @returns True if the file type is allowed, false otherwise
 */
export function isFileTypeAllowed(fileName: string, allowVideoSelection: boolean): boolean;
export function isFileTypeAllowed(fileName: string, allowedTypes?: FileExtensions[]): boolean;
export function isFileTypeAllowed(fileName: string, arg: FileExtensions[] | boolean = MediaAllowed.ALL) {
  let allowed: readonly string[] = Array.isArray(arg) ? arg : MediaAllowed.VIDEOS;
  const fileExtension = getFileExtension(fileName);
  return allowed.includes(fileExtension)
}

/**
 * Gets a human-readable file type description
 * @param fileName The name of the file
 * @returns A string describing the file type
 */
export function getFileTypeDescription(fileName: string): string {
  if (isImageFile(fileName)) return 'Image';
  if (isVideoFile(fileName)) return 'Video';
  return 'File';
}
